/**
 * Global Data Context
 * 
 * Provides cached data that persists across route changes.
 * Prevents unnecessary API calls when navigating between pages.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { authAPI, businessAPI, apiUtils, favoritesAPI } from '../services/api';
import { preferenceEngine } from '../services/preferenceEngine';
import { syncOnboardingFromCloud } from '../components/ui/QuickOnboarding';
import { getProfessionalProfile, upsertProfessionalProfile, isProfileComplete } from '../services/professionalProfileService';

const DataContext = createContext(null);

// Cache TTL in milliseconds
const CACHE_TTL = {
    user: 5 * 60 * 1000,      // 5 minutes for user data
    businesses: 3 * 60 * 1000, // 3 minutes for business list
    business: 5 * 60 * 1000,   // 5 minutes for single business
};

export const DataProvider = ({ children }) => {
    // User state
    const [user, setUser] = useState(() => apiUtils.getCurrentUser());
    const [userLoading, setUserLoading] = useState(false);
    const userFetchedAt = useRef(null);

    // Businesses state (for any user with businesses)
    const [myBusinesses, setMyBusinesses] = useState([]);           // negocios propios (is_owner=true)
    const [myAssociatedBusinesses, setMyAssociatedBusinesses] = useState([]); // negocios asociados (is_owner=false)
    const [activeAssociations, setActiveAssociations] = useState([]); // asociaciones activas con permisos
    const [businessesLoading, setBusinessesLoading] = useState(false);
    const businessesFetchedAt = useRef(null);
    const associationsFetchedAt = useRef(null);

    // Professional profile state
    // TODO-BACKEND: reemplazar con endpoint /api/professional-profile/ cuando esté listo
    // Solución temporal: Supabase para perfil profesional multi-usuario
    const [professionalProfile, setProfessionalProfile] = useState(null);
    const [professionalProfileLoading, setProfessionalProfileLoading] = useState(false);
    const professionalProfileFetchedAt = useRef(null);

    // Single business cache
    const businessCache = useRef(new Map());

    // Refs to hold current values for stable callbacks
    const userRef = useRef(user);
    const userLoadingRef = useRef(userLoading);
    const myBusinessesRef = useRef(myBusinesses);
    const myAssociatedBusinessesRef = useRef(myAssociatedBusinesses);
    const businessesLoadingRef = useRef(businessesLoading);

    // Keep refs in sync with state
    useEffect(() => { userRef.current = user; }, [user]);
    useEffect(() => { userLoadingRef.current = userLoading; }, [userLoading]);
    useEffect(() => { myBusinessesRef.current = myBusinesses; }, [myBusinesses]);
    useEffect(() => { myAssociatedBusinessesRef.current = myAssociatedBusinesses; }, [myAssociatedBusinesses]);
    useEffect(() => { businessesLoadingRef.current = businessesLoading; }, [businessesLoading]);

    // Check if cache is still valid
    const isCacheValid = (fetchedAt, ttl) => {
        if (!fetchedAt) return false;
        return Date.now() - fetchedAt < ttl;
    };

    // Fetch user profile (with cache) — stable reference
    const fetchUser = useCallback(async (force = false) => {
        const currentUser = userRef.current;
        const isLoading = userLoadingRef.current;

        // Return cached if valid and not forced
        if (!force && currentUser && isCacheValid(userFetchedAt.current, CACHE_TTL.user)) {
            return currentUser;
        }

        // Don't fetch if not authenticated
        if (!apiUtils.isAuthenticated()) {
            setUser(null);
            return null;
        }

        // Prevent duplicate requests
        if (isLoading) return currentUser;

        try {
            setUserLoading(true);
            const response = await authAPI.getProfile();
            const userData = response.user;

            setUser(userData);
            userFetchedAt.current = Date.now();
            localStorage.setItem('currentUser', JSON.stringify(userData));

            return userData;
        } catch (error) {
            console.warn('Failed to fetch user profile:', error);
            // Use cached data on error
            const cached = apiUtils.getCurrentUser();
            if (cached) {
                setUser(cached);
                return cached;
            }
            return null;
        } finally {
            setUserLoading(false);
        }
    }, []); // stable: reads state via refs

    // Fetch my businesses (for any authenticated user) — stable reference
    const fetchMyBusinesses = useCallback(async (force = false) => {
        const currentBusinesses = myBusinessesRef.current;
        const isLoading = businessesLoadingRef.current;

        // Return cached if valid and not forced — incluye propias + asociadas
        const currentAssociated = myAssociatedBusinessesRef.current;
        if (!force && (currentBusinesses.length > 0 || currentAssociated.length > 0) && isCacheValid(businessesFetchedAt.current, CACHE_TTL.businesses)) {
            return [...currentBusinesses, ...currentAssociated];
        }

        // Check user from ref, fallback to localStorage
        const currentUser = userRef.current || apiUtils.getCurrentUser();
        if (!currentUser || !apiUtils.isAuthenticated()) {
            return [];
        }

        // Prevent duplicate requests (force=true bypasses this to garantizar re-fetch tras token refresh)
        if (!force && isLoading) {
            return currentBusinesses;
        }

        try {
            // Solo mostrar loading si NO hay datos previos — evita que PermissionRoute
            // desmonte hijos durante un refresh en background (force=true con datos ya cargados)
            if (currentBusinesses.length === 0 && currentAssociated.length === 0) {
                setBusinessesLoading(true);
            }
            const businesses = await businessAPI.getMyBusinesses();
            // El backend puede devolver array directo o { count, results: [...] }
            const allBusinesses = Array.isArray(businesses)
                ? businesses
                : (businesses?.results || []);

            // Deduplicar por ID (el backend puede devolver duplicados)
            const seen = new Set();
            const unique = allBusinesses.filter(b => {
                if (seen.has(b.id)) return false;
                seen.add(b.id);
                return true;
            });

            // El backend retorna is_owner=true para propias, is_owner=false para asociadas
            const owned = unique.filter(b => b.is_owner !== false);
            const associated = unique.filter(b => b.is_owner === false);

            setMyBusinesses(owned);
            setMyAssociatedBusinesses(associated);
            businessesFetchedAt.current = Date.now();

            return allBusinesses;
        } catch (error) {
            console.warn('Failed to fetch businesses:', error);
            // Preservar tanto propias como asociadas en caso de error
            return [...currentBusinesses, ...myAssociatedBusinessesRef.current];
        } finally {
            setBusinessesLoading(false);
        }
    }, []); // stable: reads state via refs

    // Fetch active associations (with permissions) — stable reference
    // Derivar permisos extra del prefijo de rol en request_message
    // (el backend no soporta estos campos, se infieren del rol)
    const enrichAssociation = (assoc) => {
        const msg = assoc.request_message || '';
        const isAdmin = msg.startsWith('[Admin empresa]');
        const isVentas = msg.startsWith('[Ventas/Cotizador]');
        const isMarketing = msg.startsWith('[Marketing]');
        const isRRHH = msg.startsWith('[RRHH/Empleos]');
        return {
            ...assoc,
            can_edit_products: assoc.can_edit_products || isAdmin || isVentas || false,
            can_edit_services: assoc.can_edit_services || isAdmin || isVentas || false,
            can_manage_reviews: assoc.can_manage_reviews || isAdmin || isMarketing || false,
            can_view_analytics: assoc.can_view_analytics || isAdmin || isMarketing || false,
            can_manage_jobs: assoc.can_manage_jobs || isAdmin || isRRHH || false,
            can_publish_blog: assoc.can_publish_blog || isAdmin || isVentas || isMarketing || false,
            // Cotizaciones: admin_empresa y ventas. Fallback al rol mientras
            // el backend termina de propagar can_respond_quotes en respuestas
            // viejas en cache.
            can_respond_quotes: assoc.can_respond_quotes || isAdmin || isVentas || false,
            // Editar perfil de empresa: admin_empresa, ventas, marketing.
            // Fallback al rol mientras el backend termina de propagar
            // can_edit_business_profile en respuestas cacheadas.
            can_edit_business_profile: assoc.can_edit_business_profile || isAdmin || isVentas || isMarketing || false,
        };
    };

    // Quitar permisos si la empresa fue rechazada por el admin. Antes
    // stripeaba todo lo que no estuviera "approved", lo que incluía las
    // preclaimed/pending — y dejaba al asociado sin perms efectivos en
    // su propia empresa propuesta (no podía ver cotizaciones, etc.).
    // Ahora sólo stripeamos 'rejected'; las pending mantienen los perms,
    // que es coherente con lo que devuelve el backend en /quote-requests
    // /my_received y otros endpoints gateados por can_edit_*.
    const stripPermissionsIfUnverified = async (associations) => {
        const bizIds = [...new Set(associations.map(a => a.business_id).filter(Boolean))];
        if (bizIds.length === 0) return associations;

        const results = await Promise.allSettled(
            bizIds.map(id => businessAPI.getById(id, true))
        );
        const bizStatus = {};
        results.forEach((r, i) => {
            if (r.status === 'fulfilled' && r.value) {
                bizStatus[bizIds[i]] = r.value.status; // 'pending' | 'approved' | 'rejected'
            }
        });

        return associations.map(assoc => {
            const st = assoc.business_id ? bizStatus[assoc.business_id] : null;
            if (st !== 'rejected') return assoc;
            return {
                ...assoc,
                can_edit_products: false,
                can_edit_services: false,
                can_manage_reviews: false,
                can_view_analytics: false,
                can_manage_jobs: false,
                can_publish_blog: false,
                can_respond_quotes: false,
                can_edit_business_profile: false,
                _business_unverified: true,
            };
        });
    };

    const fetchActiveAssociations = useCallback(async (force = false) => {
        if (!force && activeAssociations.length > 0 && isCacheValid(associationsFetchedAt.current, CACHE_TTL.businesses)) {
            return activeAssociations;
        }
        const currentUser = userRef.current || apiUtils.getCurrentUser();
        if (!currentUser || !apiUtils.isAuthenticated()) return [];
        try {
            const data = await businessAPI.getMyAssociationRequests('active');
            const raw = Array.isArray(data) ? data : [];
            const enriched = raw.map(enrichAssociation);
            const list = await stripPermissionsIfUnverified(enriched);
            setActiveAssociations(list);
            associationsFetchedAt.current = Date.now();
            return list;
        } catch {
            return activeAssociations;
        }
    }, []);

    // Get single business (with cache)
    const getBusiness = useCallback(async (businessId, force = false) => {
        const cacheKey = String(businessId);
        const cached = businessCache.current.get(cacheKey);

        // Return cached if valid and not forced
        if (!force && cached && isCacheValid(cached.fetchedAt, CACHE_TTL.business)) {
            return cached.data;
        }

        try {
            const business = await businessAPI.getById(businessId, force);

            businessCache.current.set(cacheKey, {
                data: business,
                fetchedAt: Date.now()
            });

            return business;
        } catch (error) {
            console.warn(`Failed to fetch business ${businessId}:`, error);
            return cached?.data || null;
        }
    }, []);

    // Fetch professional profile from Supabase
    const fetchProfessionalProfile = useCallback(async (force = false) => {
        if (!force && professionalProfile && isCacheValid(professionalProfileFetchedAt.current, CACHE_TTL.user)) {
            return professionalProfile;
        }
        const currentUser = userRef.current || apiUtils.getCurrentUser();
        if (!currentUser?.id || !apiUtils.isAuthenticated()) return null;
        if (professionalProfileLoading) return professionalProfile;

        try {
            setProfessionalProfileLoading(true);
            const profile = await getProfessionalProfile(currentUser.id);
            setProfessionalProfile(profile);
            professionalProfileFetchedAt.current = Date.now();
            return profile;
        } catch {
            return professionalProfile;
        } finally {
            setProfessionalProfileLoading(false);
        }
    }, []);

    // Save professional profile to Supabase
    const saveProfessionalProfile = useCallback(async (profileData) => {
        const currentUser = userRef.current || apiUtils.getCurrentUser();
        if (!currentUser?.id) throw new Error('Usuario no autenticado');

        const saved = await upsertProfessionalProfile({
            ...profileData,
            user_id: currentUser.id,
        });
        setProfessionalProfile(saved);
        professionalProfileFetchedAt.current = Date.now();
        return saved;
    }, []);

    // Clear all caches (useful for logout)
    const clearCache = useCallback(() => {
        setUser(null);
        setMyBusinesses([]);
        setMyAssociatedBusinesses([]);
        setActiveAssociations([]);
        setProfessionalProfile(null);
        userFetchedAt.current = null;
        businessesFetchedAt.current = null;
        associationsFetchedAt.current = null;
        professionalProfileFetchedAt.current = null;
        businessCache.current.clear();
        // Invalidate favorites cache
        favoritesAPI.invalidateCache();
        // Also clear sensitive form drafts to prevent cross-user data leaks
        localStorage.removeItem('business_registration_draft');
    }, []);

    // Update user data (after profile edit)
    const updateUser = useCallback((newUserData) => {
        setUser(newUserData);
        userFetchedAt.current = Date.now();
        localStorage.setItem('currentUser', JSON.stringify(newUserData));
    }, []);

    // Invalidate businesses cache (after creating/updating business)
    const invalidateBusinesses = useCallback(() => {
        businessesFetchedAt.current = null;
    }, []);

    // Invalidate single business cache
    const invalidateBusiness = useCallback((businessId) => {
        businessCache.current.delete(String(businessId));
    }, []);

    // Pre-fetch user and professional profile on mount if authenticated
    useEffect(() => {
        if (apiUtils.isAuthenticated() && !user) {
            fetchUser();
            fetchProfessionalProfile();
        }
    }, []);

    // Clear caches when auth token changes (login/logout/switch user)
    useEffect(() => {
        const currentToken = localStorage.getItem('authToken');
        const tokenRef = { value: currentToken };

        const checkTokenChange = (e) => {
            const newToken = localStorage.getItem('authToken');
            if (newToken !== tokenRef.value) {
                tokenRef.value = newToken;

                // Un refresh silencioso (token JWT renovado automáticamente) no debe
                // vaciar las empresas — el usuario es el mismo, solo cambió el access token.
                const isSilentRefresh = e?.detail?.silent === true;

                if (!isSilentRefresh) {
                    // Login / logout explícito → limpiar todo
                    setUser(null);
                    setMyBusinesses([]);
                    setMyAssociatedBusinesses([]);
                    userFetchedAt.current = null;
                    businessesFetchedAt.current = null;
                    businessCache.current.clear();
                    favoritesAPI.invalidateCache();
                } else {
                    // Refresh silencioso → solo invalidar caché para re-fetch en background
                    userFetchedAt.current = null;
                    businessesFetchedAt.current = null;
                }

                if (newToken) {
                    fetchUser(true);
                    fetchMyBusinesses(true);
                    fetchProfessionalProfile(true);
                    // Sync preferences from cloud on login
                    preferenceEngine.pullFromCloud().catch(() => {});
                    syncOnboardingFromCloud().catch(() => {});
                }
            }
        };

        // Cross-tab changes (storage event fires when another tab modifies localStorage)
        window.addEventListener('storage', checkTokenChange);
        // Same-tab changes (custom event dispatched by auth code when token changes)
        window.addEventListener('auth-token-change', checkTokenChange);

        return () => {
            window.removeEventListener('storage', checkTokenChange);
            window.removeEventListener('auth-token-change', checkTokenChange);
        };
    }, []);

    const isAuth = apiUtils.isAuthenticated();

    const value = useMemo(() => ({
        // User
        user,
        userLoading,
        fetchUser,
        updateUser,

        // Businesses
        myBusinesses,
        myAssociatedBusinesses,
        activeAssociations,
        businessesLoading,
        fetchMyBusinesses,
        fetchActiveAssociations,
        getBusiness,

        // Professional profile
        professionalProfile,
        professionalProfileLoading,
        fetchProfessionalProfile,
        saveProfessionalProfile,
        hasProfessionalProfile: isProfileComplete(professionalProfile),

        // Cache management
        clearCache,
        invalidateBusinesses,
        invalidateBusiness,

        // Helper
        isAuthenticated: isAuth,
    }), [
        user, userLoading, isAuth,
        myBusinesses, myAssociatedBusinesses, activeAssociations, businessesLoading,
        professionalProfile, professionalProfileLoading,
        fetchUser, updateUser, fetchMyBusinesses, fetchActiveAssociations, getBusiness,
        fetchProfessionalProfile, saveProfessionalProfile,
        clearCache, invalidateBusinesses, invalidateBusiness,
    ]);

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};

// Custom hook to use the data context
export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};

// Convenience hooks
export const useUser = () => {
    const { user, userLoading, fetchUser, updateUser } = useData();
    return { user, loading: userLoading, refresh: () => fetchUser(true), update: updateUser };
};

export const useMyBusinesses = () => {
    const { myBusinesses, myAssociatedBusinesses, businessesLoading, fetchMyBusinesses, invalidateBusinesses } = useData();
    return {
        businesses: myBusinesses,
        associatedBusinesses: myAssociatedBusinesses,
        loading: businessesLoading,
        refresh: () => fetchMyBusinesses(true),
        invalidate: invalidateBusinesses
    };
};

export default DataContext;
